import { useState, useEffect, useCallback } from 'react';
import { bffQuery, bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';

// Reads/writes the wallet low-balance alert config (BFF: lowBalanceConfig /
// updateLowBalanceThreshold). Threshold is in coins. The red ≤50 floor is a
// fixed client rule (constants/credit); this threshold drives the amber warning.
const CONFIG_QUERY = `
  query LowBalanceConfig($ownerId: String!, $ownerType: String!) {
    lowBalanceConfig(ownerId: $ownerId, ownerType: $ownerType) {
      threshold enableAlerts alertEmail alertPhone
    }
  }
`;

const UPDATE_MUTATION = `
  mutation UpdateLowBalance($ownerId: String!, $ownerType: String!, $threshold: Float!, $enableAlerts: Boolean!) {
    updateLowBalanceThreshold(ownerId: $ownerId, ownerType: $ownerType, threshold: $threshold, enableAlerts: $enableAlerts) {
      threshold enableAlerts
    }
  }
`;

export function useLowBalanceConfig(doctorId) {
  const [config, setConfig] = useState(null); // { threshold, enableAlerts }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!doctorId) return;
    setLoading(true);
    bffQuery(CONFIG_QUERY, { ownerId: doctorId, ownerType: 'doctor' })
      .then(res => {
        const c = res?.lowBalanceConfig;
        setConfig(c ? { threshold: c.threshold, enableAlerts: c.enableAlerts } : { threshold: 200, enableAlerts: true });
        setError(null);
      })
      .catch(err => setError(err))
      .finally(() => setLoading(false));
  }, [doctorId]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (next) => {
    if (!doctorId) return false;
    setSaving(true);
    try {
      await bffMutation(UPDATE_MUTATION, {
        ownerId: doctorId, ownerType: 'doctor',
        threshold: Number(next.threshold), enableAlerts: !!next.enableAlerts,
      });
      setConfig({ threshold: Number(next.threshold), enableAlerts: !!next.enableAlerts });
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setSaving(false);
    }
  }, [doctorId]);

  return { config, loading, error, saving, reload: load, save };
}
